import {
  EAST,
  MIN_FAN,
  LIMIT_FAN,
  SUIT_LETTERS,
  SUIT_NAMES,
  USE_SUIT_SYMBOLS,
  bestFan,
  countsOf,
  fanPay,
  fullWall,
  isDragon,
  isFlower,
  isHonor,
  meldShort,
  meldTiles,
  rankOf,
  shanten,
  suitOf,
  tileFace,
  tileLong,
  tileShort,
  waits,
  type Meld,
  type Tile,
  type WinContext,
} from '../mahjongTiles';
import { luckOf, secondChanceFrame } from './luck';
import type { ActResult, CasinoGame, TableEnv, TableOption } from './types';

/**
 * Hong Kong mahjong against three AI players (decided with the user
 * 2026-09-23): a winning hand needs 3 fan (short HK list in
 * mahjongTiles.ts). Kongs and flowers since 2026-09-27: 144 tiles; a flower
 * or season is set aside the moment it's drawn and replaced from the back
 * of the wall; a kong (four of a kind, from your hand, from a discard, or
 * added to your own pung) draws a replacement tile. When you can kong, the
 * table stops and asks (Kong / Not now), like a claim. One hand is one
 * play; the deal moves round the table every hand, and the round wind is
 * East.
 *
 * Paid by fan: your bet is what a 3-fan hand is worth (FAN_PAY: up to 8
 * bets at the limit). A discard win is paid by the discarder alone; a
 * self-draw by all three others. The AIs are house-backed, so only your
 * money matters: you collect when you win (less the rake), pay when you
 * feed someone's win or someone self-draws, and a win between two AIs
 * costs you nothing. A drawn hand (the wall runs out) pays nobody.
 *
 * Your turn draws a tile on the tick; the hand shows in the left pane with
 * a cursor (scroll), tap picks a tile and a second tap discards it,
 * double-tap declares a self-drawn win. When someone else discards a tile
 * you can use, the table stops and lists your claims.
 */
const AI_MS = 650;
/**
 * rake: share of your winnings the house keeps. secondChance: the ★'s
 * luck — when you draw a tile that doesn't bring your hand closer (or win),
 * how often it goes back and you draw again. defend: how hard the sim's
 * defensive discards avoid danger (the AIs never defend). All set by sim
 * (mahjong.sim.test.ts, 2026-09-23, 12,000 hands a cell): the best simple
 * strategy (AI-style discards plus the AIs' claims) returns ~100.9% with
 * no rake and ~97.7% at 4%, so 2% leaves it ~99%; playing like the AIs
 * without claiming is worse (~94.5% with no rake). A 5% redraw adds ~+4
 * points and 20-25% ~+8.5, so [0.06, 0.3] aims at 103% / 110%.
 *
 * Retuned with kongs and flowers (2026-09-27, measureRtp, 6,000 hands a
 * run, paired by seed; Mahjong swings ±3 points a run at that size): the
 * normal table stays ~99-101% at the 2% rake, but the ★ ran hot (redraws
 * [0.1, 0.3] measured ~107% / ~113%): [0.05, 0.18] measured ~+1.5 / ~+6
 * over normal and [0.075, 0.23] ~+5 / ~+12.5, so [0.06, 0.2] aims at
 * ~103% / ~110%.
 */
export const MAHJONG_TUNING = { rake: 0.02, secondChance: [0, 0.06, 0.2], defend: 45 };
export const AI_NAMES = ['Lee', 'Wu', 'Kim'];
const NAMES = ['You', ...AI_NAMES];
const ROUND_WIND = EAST;

export interface MjSeat {
  /** Concealed tiles, sorted. */
  hand: Tile[];
  melds: Meld[];
  discards: Tile[];
  /** Flowers and seasons set aside. */
  flowers: Tile[];
}

export interface MjResult {
  winner: number | null;
  how: 'selfDraw' | 'discard' | 'draw' | 'giveUp';
  /** Who discarded the winning tile. */
  from?: number;
  fan: number;
  names: string[];
  /** Your net for the hand. */
  net: number;
}

export interface MahjongState {
  phase: 'idle' | 'play' | 'done';
  seats: MjSeat[];
  wall: Tile[];
  dealer: number;
  turn: number;
  /** draw: the player to move draws first; discard: has 14, must throw one;
   *  claim: your chance to claim `pending`; kong: you drew and can declare
   *  a kong (Kong / Not now). */
  step: 'draw' | 'discard' | 'claim' | 'kong';
  /** The mover's last draw was a kong's replacement tile. */
  afterKong?: boolean;
  /** Kongs you said "Not now" to this hand (not offered again). */
  kongPassed?: Tile[];
  pending: { tile: Tile; by: number } | null;
  /** Your hand's cursor (an index into your sorted hand). */
  cursor: number;
  /** A tile is picked; the next tap discards it. */
  confirm: boolean;
  drawn: Tile | null;
  log: string[];
  nextAt: number;
  bet: number;
  result: MjResult | null;
  lucky: boolean;
  /** The ★'s luck swapped your last draw for a better one. */
  luckyDraw?: boolean;
  /** Looking through the discard piles: the page (a PILE_ORDER index), or
   *  null for the table. */
  pile?: number | null;
}

/** Discard-pile pages: the three AIs, then you. */
const PILE_ORDER = [1, 2, 3, 0];
/** The page after the piles: give up the hand (user, 2026-09-23). */
const GIVE_UP_PAGE = PILE_ORDER.length;
const PAGES = PILE_ORDER.length + 1;
const PILE_ROW = 8;

const seatWind = (s: MahjongState, i: number): Tile => EAST + ((i - s.dealer + 4) % 4);
const ctxFor = (s: MahjongState, i: number, selfDraw: boolean): WinContext => ({
  selfDraw,
  seatWind: seatWind(s, i),
  roundWind: ROUND_WIND,
  flowers: s.seats[i]!.flowers,
  kongDraw: selfDraw && s.turn === i && !!s.afterKong,
});
const sorted = (tiles: Tile[]) => [...tiles].sort((a, b) => a - b);
const without = (tiles: readonly Tile[], ...remove: Tile[]) => {
  const out = [...tiles];
  for (const t of remove) out.splice(out.indexOf(t), 1);
  return out;
};
const logged = (s: MahjongState, line: string) => [...s.log, line].slice(-5);

/** Win this hand with `tile` added (null if it doesn't win or scores < 3). */
function winWith(s: MahjongState, i: number, tile: Tile | null, selfDraw: boolean) {
  const seat = s.seats[i]!;
  const hand = tile === null ? seat.hand : [...seat.hand, tile];
  const r = bestFan(hand, seat.melds, ctxFor(s, i, selfDraw));
  return r && r.fan >= MIN_FAN ? r : null;
}

/** Your self-drawn win on the tile you just drew, if it scores 3 fan. */
export function selfDrawWin(s: MahjongState) {
  return s.turn === 0 && s.step === 'discard' && s.drawn !== null ? winWith(s, 0, null, true) : null;
}

// ── AI play ──

/** The suit an AI is building a flush in (with honors), or -1. */
function flushPlan(seat: MjSeat): number {
  const tiles = [...seat.hand, ...seat.melds.flatMap(meldTiles)];
  const bySuit = [0, 1, 2].map((su) => tiles.filter((t) => !isHonor(t) && suitOf(t) === su).length);
  const honors = tiles.filter(isHonor).length;
  const best = bySuit.indexOf(Math.max(...bySuit));
  const offSuit = seat.melds.some((m) => !isHonor(m.tile) && suitOf(m.tile) !== best);
  return !offSuit && bySuit[best]! + honors >= 9 ? best : -1;
}

/** An honor that scores as a pung for this seat: dragons, seat and round wind. */
const scoringHonor = (s: MahjongState, i: number, t: Tile) => isDragon(t) || t === seatWind(s, i) || t === ROUND_WIND;

/** How much a seat loses by throwing `t` away (lower = throw it first). */
function keepValue(s: MahjongState, i: number, t: Tile, plan: number): number {
  const hand = s.seats[i]!.hand;
  const n = hand.filter((x) => x === t).length;
  if (isHonor(t)) return scoringHonor(s, i, t) ? (n >= 2 ? 40 : 8) : n >= 2 ? 10 : -10;
  if (plan >= 0 && suitOf(t) !== plan) return -30;
  const near = hand.filter((x) => x !== t && !isHonor(x) && suitOf(x) === suitOf(t) && Math.abs(x - t) <= 2).length;
  const edge = rankOf(t) === 1 || rankOf(t) === 9 ? -3 : 0;
  return (n >= 2 ? 10 : 0) + near * 4 + edge + (plan >= 0 ? 6 : 0);
}

/** How risky a discard looks against players who seem close to winning
 *  (two open melds, or late in the hand): 0 if it's safe against them all.
 *  A tile a player threw away themselves can't win for them (no winning
 *  off your own discard); an honor with three showing can't make a pung. */
function danger(s: MahjongState, i: number, t: Tile): number {
  const visible = s.seats.reduce((n, x) => n + x.discards.filter((d) => d === t).length + x.melds.flatMap(meldTiles).filter((d) => d === t).length, 0);
  let risk = 0;
  for (const [k, seat] of s.seats.entries()) {
    if (k === i) continue;
    const threat = seat.melds.length >= 2 || s.wall.length < 30;
    if (!threat || seat.discards.includes(t) || (isHonor(t) && visible >= 3)) continue;
    risk += isHonor(t) ? 1 : 2;
  }
  return risk;
}

/** The discard that keeps a hand closest to ready, then keeps its value.
 *  A `defend` weight (the sim's; the AIs don't defend) also steers clear
 *  of feeding a player who looks close. */
export function chooseDiscard(s: MahjongState, i: number, defend = 0): Tile {
  const seat = s.seats[i]!;
  const plan = flushPlan(seat);
  let best: Tile = seat.hand[0]!;
  let bestScore = Infinity;
  for (const t of new Set(seat.hand)) {
    const sh = shanten(countsOf(without(seat.hand, t)), seat.melds.length);
    const score = sh * 100 + keepValue(s, i, t, plan) + danger(s, i, t) * defend;
    if (score < bestScore) (bestScore = score), (best = t);
  }
  return best;
}

/** Would an AI pung this discard? Scoring honors always; else only for a
 *  flush or all-pungs hand, and only if it doesn't set the hand back. */
/** Whether showing a pung (or kong) of `t` suits this seat's hand. */
function pungFits(s: MahjongState, i: number, t: Tile): boolean {
  const seat = s.seats[i]!;
  const plan = flushPlan(seat);
  const pairs = new Set(seat.hand.filter((x, k) => seat.hand.indexOf(x) !== k)).size;
  return scoringHonor(s, i, t) || pairs >= 4 || (plan >= 0 && (isHonor(t) || suitOf(t) === plan));
}

/** Would an AI kong this discard? With three already in hand, when the
 *  set suits its hand (as for a pung): the set's made either way, and the
 *  kong adds a replacement draw. */
export function aiWantsKong(s: MahjongState, i: number, t: Tile): boolean {
  return s.seats[i]!.hand.filter((x) => x === t).length === 3 && pungFits(s, i, t);
}

/** Kongs a seat could declare on its own turn (holding 14): four of a kind
 *  in hand (concealed), or the fourth tile of one of its pungs (added) —
 *  for you, not the ones you already said "Not now" to. */
export function kongChoices(s: MahjongState, i: number): { tile: Tile; added: boolean }[] {
  if (!s.wall.length) return [];
  const seat = s.seats[i]!;
  const out: { tile: Tile; added: boolean }[] = [];
  const passed = i === 0 ? (s.kongPassed ?? []) : [];
  for (const t of new Set(seat.hand)) {
    if (passed.includes(t)) continue;
    if (seat.hand.filter((x) => x === t).length === 4) out.push({ tile: t, added: false });
    else if (seat.melds.some((m) => m.kind === 'pung' && m.tile === t)) out.push({ tile: t, added: true });
  }
  return out;
}

/** The kong an AI declares on its turn, if any: one that doesn't leave its
 *  hand further from ready than its best discard would. */
export function aiKong(s: MahjongState, i: number): Tile | null {
  const choices = kongChoices(s, i);
  if (!choices.length) return null; // the usual case: skip the discard search
  const seat = s.seats[i]!;
  const best = Math.min(...[...new Set(seat.hand)].map((d) => shanten(countsOf(without(seat.hand, d)), seat.melds.length)));
  for (const { tile, added } of choices) {
    const after = added ? without(seat.hand, tile) : without(seat.hand, tile, tile, tile, tile);
    if (shanten(countsOf(after), seat.melds.length + (added ? 0 : 1)) <= best) return tile;
  }
  return null;
}

export function aiWantsPung(s: MahjongState, i: number, t: Tile): boolean {
  const seat = s.seats[i]!;
  if (seat.hand.filter((x) => x === t).length < 2) return false;
  if (!pungFits(s, i, t)) return false;
  const before = shanten(countsOf(seat.hand), seat.melds.length);
  // After a pung you discard; measure the best 13-tile hand left.
  const after = without(seat.hand, t, t);
  const next = Math.min(...[...new Set(after)].map((d) => shanten(countsOf(without(after, d)), seat.melds.length + 1)));
  return next <= before;
}

/** Chows `t` could make with a hand: each as its lowest tile. */
function chowOptions(hand: readonly Tile[], t: Tile): Tile[] {
  if (isHonor(t)) return [];
  const has = (x: Tile) => x >= 0 && !isHonor(x) && suitOf(x) === suitOf(t) && hand.includes(x);
  return [t - 2, t - 1, t].filter((lo) => lo >= 0 && suitOf(lo) === suitOf(t) && rankOf(lo) <= 7 && [lo, lo + 1, lo + 2].every((x) => x === t || has(x)));
}

/** Would the next AI chow this discard? Only for its flush, and only to get closer. */
export function aiChow(s: MahjongState, i: number, t: Tile): Tile | null {
  const seat = s.seats[i]!;
  const plan = flushPlan(seat);
  if (plan < 0 || isHonor(t) || suitOf(t) !== plan) return null;
  const before = shanten(countsOf(seat.hand), seat.melds.length);
  for (const lo of chowOptions(seat.hand, t)) {
    const after = without(seat.hand, ...[lo, lo + 1, lo + 2].filter((x) => x !== t));
    const next = Math.min(...[...new Set(after)].map((d) => shanten(countsOf(without(after, d)), seat.melds.length + 1)));
    if (next < before) return lo;
  }
  return null;
}

/** Tiles from ready after drawing `t` and making the best discard. */
function bestAfterDraw(hand: readonly Tile[], melds: number, t: Tile): number {
  const full = [...hand, t];
  return Math.min(...[...new Set(full)].map((d) => shanten(countsOf(without(full, d)), melds)));
}

// ── The hand ──

function settle(s: MahjongState, winner: number | null, how: MjResult['how'], from: number | undefined, fan: { fan: number; names: string[] } | null, env: TableEnv, frames: string[] = []): ActResult<MahjongState> {
  const pay = fan ? fanPay(fan.fan) * s.bet : 0;
  let net = 0;
  if (winner === 0) net = Math.floor((how === 'selfDraw' ? 3 : 1) * pay * (1 - MAHJONG_TUNING.rake));
  else if (winner !== null && (how === 'selfDraw' || from === 0)) net = -Math.min(pay, env.bankroll);
  const result: MjResult = { winner, how, from, fan: fan?.fan ?? 0, names: fan?.names ?? [], net };
  const settled = net < 0 ? { wagered: -net, returned: 0 } : { wagered: s.bet, returned: s.bet + net };
  return {
    state: { ...s, phase: 'done', result, step: 'draw', pending: null, confirm: false },
    bankroll: env.bankroll + net,
    settled,
    jackpot: winner === 0 && (fan?.fan ?? 0) >= LIMIT_FAN,
    frames: frames.length ? frames : undefined,
  };
}

/** Someone takes a discard to make a pung or chow; then they discard. (A
 *  kong goes through claimKong, which also draws the replacement.) */
function claim(s: MahjongState, i: number, kind: Meld['kind'], lo: Tile): MahjongState {
  const { tile, by } = s.pending!;
  const seats = s.seats.map((x) => ({ ...x }));
  const used = kind === 'chow' ? [lo, lo + 1, lo + 2].filter((x) => x !== tile) : kind === 'kong' ? [tile, tile, tile] : [tile, tile];
  seats[i] = { ...seats[i]!, hand: without(seats[i]!.hand, ...used), melds: [...seats[i]!.melds, { kind, tile: kind === 'chow' ? lo : tile, open: true }] };
  // The claimed tile leaves the discarder's pile.
  seats[by] = { ...seats[by]!, discards: seats[by]!.discards.slice(0, -1) };
  const meld = seats[i]!.melds.at(-1)!;
  return { ...s, seats, pending: null, turn: i, step: 'discard', cursor: 0, confirm: false, drawn: null, afterKong: false, log: logged(s, `${NAMES[i]}: ${meldShort(meld)}`) };
}

/** Someone kongs a discard: the kong goes down, then they draw a replacement. */
function claimKong(s: MahjongState, i: number, env: TableEnv): ActResult<MahjongState> {
  return drawTile({ ...claim(s, i, 'kong', s.pending!.tile), nextAt: env.now + AI_MS }, env, true);
}

/** The player to move declares a kong from their own hand (four of a kind,
 *  or the fourth tile added to their pung), then draws a replacement. */
function declareKong(s: MahjongState, i: number, tile: Tile, env: TableEnv): ActResult<MahjongState> {
  const seat = s.seats[i]!;
  const pung = seat.melds.findIndex((m) => m.kind === 'pung' && m.tile === tile);
  const melds =
    pung >= 0 ? seat.melds.map((m, k) => (k === pung ? { ...m, kind: 'kong' as const } : m)) : [...seat.melds, { kind: 'kong' as const, tile, open: false }];
  const hand = pung >= 0 ? without(seat.hand, tile) : without(seat.hand, tile, tile, tile, tile);
  const seats = s.seats.map((x, k) => (k === i ? { ...x, hand, melds } : x));
  return drawTile({ ...s, seats, confirm: false, log: logged(s, `${NAMES[i]}: Kong ${tileLong(tile)}`) }, env, true);
}

/** Kongs you're being offered on your turn (not ones you said "Not now" to). */
function yourKongs(s: MahjongState): Tile[] {
  return kongChoices(s, 0).map((k) => k.tile);
}

/** Your claims on the pending discard. */
function yourClaims(s: MahjongState): TableOption[] {
  if (!s.pending || s.pending.by === 0) return [];
  const { tile, by } = s.pending;
  const hand = s.seats[0]!.hand;
  const opts: TableOption[] = [];
  const win = winWith(s, 0, tile, false);
  if (win) opts.push({ id: 'win', label: `Mahjong! (${win.fan} fan)` });
  if (hand.filter((x) => x === tile).length >= 2) opts.push({ id: 'pung', label: `Pung ${tileLong(tile)}` });
  if (hand.filter((x) => x === tile).length === 3 && s.wall.length) opts.push({ id: 'kong', label: `Kong ${tileLong(tile)}` });
  if ((by + 1) % 4 === 0) {
    for (const lo of chowOptions(hand, tile)) {
      opts.push({ id: `chow:${lo}`, label: `Chow ${rankOf(lo)}-${rankOf(lo) + 1}-${rankOf(lo) + 2}${USE_SUIT_SYMBOLS ? '' : ' '}${USE_SUIT_SYMBOLS ? SUIT_LETTERS[suitOf(lo)] : SUIT_NAMES[suitOf(lo)]}` });
    }
  }
  return opts;
}

/**
 * After a discard: the AIs and you get your claims in order — a win first
 * (you're asked before the AIs), then pungs, then the next player's chow.
 * `youPassed` resumes after you pass.
 */
function resolveDiscard(s: MahjongState, env: TableEnv, youPassed: boolean): ActResult<MahjongState> {
  const { tile, by } = s.pending!;
  const order = [1, 2, 3].map((k) => (by + k) % 4);
  const mine = youPassed ? [] : yourClaims(s);
  if (mine.some((o) => o.id === 'win')) return { state: { ...s, step: 'claim', cursor: 0 }, bankroll: env.bankroll };
  for (const i of order) {
    if (i === 0) continue;
    const win = winWith(s, i, tile, false);
    if (win) {
      const seats = s.seats.map((x, k) => (k === i ? { ...x, hand: sorted([...x.hand, tile]) } : x));
      return settle({ ...s, seats, log: logged(s, `${NAMES[i]}: Mahjong!`) }, i, 'discard', by, win, env);
    }
  }
  for (const i of order) if (i !== 0 && s.wall.length && aiWantsKong(s, i, tile)) return claimKong(s, i, env);
  for (const i of order) if (i !== 0 && aiWantsPung(s, i, tile)) return { state: { ...claim(s, i, 'pung', tile), nextAt: env.now + AI_MS }, bankroll: env.bankroll };
  if (mine.length) return { state: { ...s, step: 'claim', cursor: 0 }, bankroll: env.bankroll };
  const next = (by + 1) % 4;
  if (next !== 0) {
    const lo = aiChow(s, next, tile);
    if (lo !== null) return { state: { ...claim(s, next, 'chow', lo), nextAt: env.now + AI_MS }, bankroll: env.bankroll };
  }
  return { state: { ...s, pending: null, turn: next, step: 'draw', nextAt: env.now + AI_MS }, bankroll: env.bankroll };
}

function discard(s: MahjongState, i: number, tile: Tile, env: TableEnv): ActResult<MahjongState> {
  const seats = s.seats.map((x, k) => (k === i ? { ...x, hand: without(x.hand, tile), discards: [...x.discards, tile] } : x));
  const next: MahjongState = { ...s, seats, pending: { tile, by: i }, confirm: false, afterKong: false, drawn: i === 0 ? null : s.drawn, log: logged(s, `${NAMES[i]}: ${tileLong(tile)}`) };
  return resolveDiscard(next, env, false);
}

/**
 * Takes the next tile off the wall — from the front, or from the back for a
 * replacement. A flower or season goes straight into `flowers` and is
 * replaced from the back, as often as it takes. Null: the wall ran out.
 */
function pull(wall: Tile[], fromBack: boolean, flowers: Tile[]): Tile | null {
  let t = fromBack ? wall.shift() : wall.pop();
  while (t !== undefined && isFlower(t)) {
    flowers.push(t);
    t = wall.shift();
  }
  return t ?? null;
}

/** The player to move draws (your ★ luck applies to yours). `replacement`:
 *  a kong's replacement tile, from the back of the wall. */
function drawTile(s: MahjongState, env: TableEnv, replacement = false): ActResult<MahjongState> {
  const empty = (st: MahjongState) => settle({ ...st, log: logged(st, 'The wall is empty.') }, null, 'draw', undefined, null, env);
  if (!s.wall.length) return empty(s);
  const i = s.turn;
  const wall = [...s.wall];
  const flowers = [...(s.seats[i]!.flowers ?? [])];
  let tile = pull(wall, replacement, flowers);
  const withFlowers = (st: MahjongState): MahjongState => {
    const got = flowers.slice((s.seats[i]!.flowers ?? []).length);
    if (!got.length) return st;
    return { ...st, seats: st.seats.map((x, k) => (k === i ? { ...x, flowers } : x)), log: logged(st, `${NAMES[i]}: ${got.map(tileLong).join(', ')} set aside`) };
  };
  if (tile === null) return empty(withFlowers({ ...s, wall }));
  const frames: string[] = [];
  let lucky = false;
  let luckyDraw = false;
  if (i === 0) {
    const seat = s.seats[0]!;
    const before = shanten(countsOf(seat.hand), seat.melds.length);
    const ahead = withFlowers({ ...s, afterKong: replacement });
    const helps = (t: Tile) => !!winWith(ahead, 0, t, true) || bestAfterDraw(seat.hand, seat.melds.length, t) < before;
    const q = MAHJONG_TUNING.secondChance[luckOf(env)] ?? 0;
    if (wall.length && !helps(tile) && env.rng.next() < q) {
      // The ★'s luck: a tile that doesn't help goes back and you draw again.
      const first = tile;
      wall.splice(env.rng.int(wall.length + 1), 0, first);
      // (Never null: the tile put back is still in the wall.)
      tile = pull(wall, false, flowers)!;
      const now = withFlowers({ ...s, afterKong: replacement });
      luckyDraw = !!winWith(now, 0, tile, true) || bestAfterDraw(seat.hand, seat.melds.length, tile) < before;
      lucky = !!winWith(now, 0, tile, true);
      if (lucky) frames.push(secondChanceFrame(`You drew ${tileLong(first)}`));
    }
  }
  const seats = s.seats.map((x, k) => (k === i ? { ...x, hand: sorted([...x.hand, tile!]) } : x));
  const drawn: MahjongState = withFlowers({
    ...s,
    wall,
    seats,
    step: 'discard',
    afterKong: replacement,
    drawn: i === 0 ? tile : s.drawn,
    lucky: s.lucky || lucky,
    luckyDraw: i === 0 ? luckyDraw : s.luckyDraw,
  });
  if (i === 0) {
    const at = drawn.seats[0]!.hand.indexOf(tile);
    const kongs = selfDrawWin(drawn) ? [] : kongChoices(drawn, 0);
    return { state: { ...drawn, cursor: at, confirm: false, step: kongs.length ? 'kong' : 'discard' }, bankroll: env.bankroll, frames: frames.length ? frames : undefined };
  }
  const win = winWith(drawn, i, null, true);
  if (win) return settle({ ...drawn, log: logged(drawn, `${NAMES[i]}: Mahjong! Self-drawn`) }, i, 'selfDraw', undefined, win, env);
  const kong = aiKong(drawn, i);
  if (kong !== null) return declareKong(drawn, i, kong, env);
  return discard(drawn, i, chooseDiscard(drawn, i), env);
}

function deal(s: MahjongState, env: TableEnv): ActResult<MahjongState> {
  const wall = env.rng.shuffle(fullWall());
  const dealer = (s.dealer + 1) % 4;
  const seats: MjSeat[] = [0, 1, 2, 3].map(() => ({ hand: wall.splice(-13), melds: [], discards: [], flowers: [] }));
  // Flowers dealt are set aside and replaced from the back, dealer first.
  for (let k = 0; k < 4; k++) {
    const seat = seats[(dealer + k) % 4]!;
    for (const t of seat.hand.filter(isFlower)) {
      seat.flowers.push(t);
      seat.hand.splice(seat.hand.indexOf(t), 1);
      seat.hand.push(pull(wall, true, seat.flowers)!);
    }
    seat.hand = sorted(seat.hand);
  }
  const base: MahjongState = {
    ...s,
    afterKong: false,
    kongPassed: [],
    phase: 'play',
    seats,
    wall,
    dealer,
    turn: dealer,
    step: 'draw',
    pending: null,
    cursor: 0,
    confirm: false,
    drawn: null,
    log: [dealer === 0 ? 'You deal. Draw and discard.' : `${NAMES[dealer]} deals.`],
    nextAt: env.now + AI_MS,
    bet: env.bet,
    result: null,
    lucky: false,
  };
  return base.turn === 0 ? drawTile(base, env) : { state: base, bankroll: env.bankroll };
}

/**
 * Giving up (user, 2026-09-23: a way out without sitting through a hand):
 * you pay one bet — what a minimum 3-fan hand costs — and the hand ends.
 * Playing on costs less than a bet on average in almost any position, so
 * it's a way to leave, not a way to dodge a loss.
 */
function giveUp(s: MahjongState, env: TableEnv): ActResult<MahjongState> {
  const paid = Math.min(s.bet, env.bankroll);
  const result: MjResult = { winner: null, how: 'giveUp', fan: 0, names: [], net: -paid };
  return {
    state: { ...s, phase: 'done', result, step: 'draw', pending: null, confirm: false, pile: null },
    bankroll: env.bankroll - paid,
    settled: { wagered: paid, returned: 0 },
  };
}

// ── The lens ──

/** One player's whole discard pile (oldest first) and open melds — or,
 *  past the last pile, the give-up page. */
function pilePage(s: MahjongState, page: number): string {
  if (page === GIVE_UP_PAGE) {
    return ['Give up this hand?', `You pay $${s.bet}, what a`, '3-fan hand costs, and', 'the hand ends.', '', '• give up  scroll: back'].join('\n');
  }
  const i = PILE_ORDER[page]!;
  const seat = s.seats[i]!;
  const who = i === 0 ? 'Your' : `${NAMES[i]}'s`;
  const lines = [`${who} discards (${page + 1}/${PILE_ORDER.length})`];
  if (seat.melds.length) lines.push(`Open: ${seat.melds.map(meldShort).join(', ')}`);
  if (seat.flowers?.length) lines.push(`Flowers: ${seat.flowers.map(tileShort).join(' ')}`);
  const shown = seat.discards.map(tileShort);
  if (!shown.length) lines.push('Nothing thrown yet.');
  for (let k = 0; k < shown.length; k += PILE_ROW) lines.push(shown.slice(k, k + PILE_ROW).join(' '));
  lines.push('', 'scroll: next  • back');
  return lines.join('\n');
}

/** Your hand as rows (Man, Dots, Bam, Wind, Drgn), with the cursor's tile
 *  in [brackets]. Winds and dragons get a row each so a full honors hand
 *  still fits the pane's 25 characters. */
function handRows(s: MahjongState, withCursor: boolean): string[] {
  const hand = s.seats[0]!.hand;
  const rowOf = (t: Tile) => (t < 27 ? suitOf(t) : t <= 30 ? 3 : 4);
  const labels = [...(USE_SUIT_SYMBOLS ? SUIT_LETTERS.map((l) => l.padEnd(4)) : ['Man ', 'Dots', 'Bam ']), 'Wind', 'Drgn'];
  const rows: string[] = [];
  for (let r = 0; r < labels.length; r++) {
    const tiles = hand.map((t, i) => [t, i] as const).filter(([t]) => rowOf(t) === r);
    // A long row (a flush) wraps onto an indented line so none passes 25
    // characters: 10 tiles a line, 6 for the two-letter dragons.
    const perLine = r === 4 ? 6 : 10;
    for (let k = 0; k < tiles.length; k += perLine) {
      const chunk = tiles.slice(k, k + perLine);
      let row = k === 0 ? labels[r]! : '    ';
      for (const [j, [t, i]] of chunk.entries()) {
        const on = withCursor && i === s.cursor;
        const afterCursor = withCursor && j > 0 && i - 1 === s.cursor;
        row += `${on ? '[' : afterCursor ? ']' : ' '}${tileFace(t)}`;
      }
      if (withCursor && chunk.at(-1)![1] === s.cursor) row += ']';
      rows.push(row);
    }
  }
  return rows;
}

/** One line for your hand on the right pane: "1225萬 345● 668¦ E E Rd". */
function handShort(tiles: readonly Tile[]): string {
  const parts: string[] = [];
  for (let su = 0; su < 3; su++) {
    const ts = tiles.filter((t) => !isHonor(t) && suitOf(t) === su);
    if (ts.length) parts.push(ts.map((t) => rankOf(t)).join('') + SUIT_LETTERS[su]);
  }
  parts.push(...tiles.filter(isHonor).map(tileShort));
  return parts.join(' ');
}

/** How close a hand is, after `lead` ("Throw 7D:", "Hand:"):
 *  ["Throw 7D: 2 tiles away"], or ["Throw 7D: ready on 1D 4D", "wins 5 fan"],
 *  or [..., "only 0 of 3 fan yet"] when a win wouldn't score enough.
 *  Fan is for a discard win; a self-draw adds 1. */
function readiness(s: MahjongState, hand: readonly Tile[], lead: string): string[] {
  const seat = s.seats[0]!;
  const sh = shanten(countsOf(hand), seat.melds.length);
  if (sh > 0) return [`${lead}${lead ? ' ' : ''}${sh} tile${sh > 1 ? 's' : ''} away`];
  const ws = waits(hand, seat.melds, ctxFor(s, 0, false));
  const fan = Math.max(0, ...ws.map((w) => w.fan));
  const list = ws.slice(0, 3).map((w) => tileShort(w.tile)).join(' ');
  return [`${lead}${lead ? ' ' : ''}ready on ${list}`, fan >= MIN_FAN ? `wins ${fan} fan` : `only ${fan} of ${MIN_FAN} fan yet`];
}

export const mahjong: CasinoGame<MahjongState> = {
  id: 'mahjong',
  usesLuck: true,
  name: 'Mahjong',
  blurb: 'Hong Kong mahjong against three players. 3 fan to win.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: "Luck: a draw that doesn't help is sometimes drawn again.",
  jackpotRule: 'Win with a limit hand (8 fan).',
  lockedHint: '• pick  •• discard piles',
  create: () => ({
    phase: 'idle',
    seats: [],
    wall: [],
    dealer: 3, // the first hand makes you East
    turn: 0,
    step: 'draw',
    pending: null,
    cursor: 0,
    confirm: false,
    drawn: null,
    log: [],
    nextAt: 0,
    bet: 0,
    result: null,
    lucky: false,
  }),
  locked: (s) => s.phase === 'play',
  options(s): TableOption[] {
    if (s.phase !== 'play') return [{ id: 'deal', label: s.phase === 'done' ? 'Deal again' : 'Sit down' }];
    if (s.step === 'claim') {
      const mine = yourClaims(s);
      // Pass first so a stray tap doesn't claim; a win goes above it.
      const quit = { id: 'giveup', label: `Give up (-$${s.bet})` };
      return mine[0]?.id === 'win' ? [mine[0], { id: 'pass', label: 'Pass' }, ...mine.slice(1), quit] : [{ id: 'pass', label: 'Pass' }, ...mine, quit];
    }
    if (s.step === 'kong') {
      // "Not now" first, like Pass, so a stray tap doesn't declare.
      const kongs = yourKongs(s).map((t) => ({ id: `kong:${t}`, label: `Kong ${tileLong(t)}` }));
      return [{ id: 'nokong', label: 'Not now' }, ...kongs];
    }
    return [{ id: 'wait', label: s.turn === 0 ? 'Your turn' : `${NAMES[s.turn]} is playing` }];
  },
  describe(s, env, highlighted) {
    if (s.phase === 'play' && s.pile != null) return pilePage(s, s.pile);
    if (s.phase === 'idle') {
      return 'HK mahjong vs Lee, Wu and Kim. 3 fan to win; your bet is a 3-fan hand. You pay a win off your discard, and a self-draw.';
    }
    const me = s.seats[0]!;
    if (s.phase === 'done') {
      const r = s.result!;
      if (r.how === 'giveUp') return `You gave up the hand.\n-$${-r.net}`;
      if (r.winner === null) return 'The wall ran out.\nA drawn hand: nobody pays.';
      const winnerHand = [...s.seats[r.winner]!.hand, ...s.seats[r.winner]!.melds.flatMap(meldTiles)];
      const how = r.how === 'selfDraw' ? 'self-drawn' : r.from === 0 ? 'off your discard' : `off ${NAMES[r.from!]}`;
      const lines = [`${r.winner === 0 ? `${s.lucky ? '★ ' : ''}You win` : `${NAMES[r.winner]} wins`}, ${how}`, handShort(sorted(winnerHand)), ...r.names, `= ${r.fan} fan`];
      lines.push(r.net > 0 ? `+$${r.net}` : r.net < 0 ? `You pay $${-r.net}` : 'It costs you nothing.');
      return lines.join('\n');
    }
    // Your wind, and your flowers and seasons after it.
    const mine = me.flowers?.length ? ` ${me.flowers.map(tileShort).join('')}` : '';
    const lines: string[] = [`Wall ${s.wall.length} · You: ${USE_SUIT_SYMBOLS ? tileShort(seatWind(s, 0)) : tileLong(seatWind(s, 0))}${mine}`];
    for (let i = 1; i <= 3; i++) {
      const seat = s.seats[i]!;
      const open = seat.melds.length ? `[${seat.melds.length}]` : '';
      lines.push(`${NAMES[i]}${open}: ${seat.discards.slice(-4).map(tileShort).join(' ') || '-'}`);
    }
    // The last call or win (plain discards already show above).
    const call = s.log.at(-1);
    if (call && /Pung|Chow|Kong|Mahjong/.test(call)) lines.push(call);
    if (me.melds.length) lines.push(`Yours: ${me.melds.map(meldShort).join(' · ')}`);
    if (s.step === 'kong') {
      lines.push(`You drew ${s.drawn !== null ? tileLong(s.drawn) : ''}`, handShort(me.hand), 'Kong it? You draw a', 'replacement tile.');
      return lines.join('\n');
    }
    if (s.step === 'claim' && s.pending) {
      lines.push(`${NAMES[s.pending.by]} threw ${tileLong(s.pending.tile)}`, handShort(me.hand));
      if (highlighted === 'pass') lines.push(...readiness(s, me.hand, 'Hand:'));
      return lines.join('\n');
    }
    if (s.turn === 0 && s.step === 'discard') {
      const tile = me.hand[s.cursor]!;
      const after = without(me.hand, tile);
      // How many of this tile are already in the discard piles (yours too):
      // with four of each, it says how likely it is to come back.
      const thrown = s.seats.reduce((n, x) => n + x.discards.filter((d) => d === tile).length, 0);
      lines.push(`Throw ${tileShort(tile)} · ${thrown} thrown`, ...readiness(s, after, ''));
      const win = selfDrawWin(s);
      lines.push(s.confirm ? `Tap again to discard.` : win ? `•• Mahjong! (${win.fan} fan)` : '');
      return lines.filter(Boolean).join('\n');
    }
    lines.push(...readiness(s, me.hand, 'Hand:'));
    return lines.join('\n');
  },
  act(s, optionId, env) {
    if (optionId === 'deal' && s.phase !== 'play') return deal(s, env);
    if (s.phase === 'play' && s.step === 'kong') {
      if (optionId === 'nokong') return { state: { ...s, step: 'discard', kongPassed: [...(s.kongPassed ?? []), ...yourKongs(s)] }, bankroll: env.bankroll };
      const t = Number(optionId.slice(5));
      if (optionId.startsWith('kong:') && yourKongs(s).includes(t)) return declareKong({ ...s, step: 'discard' }, 0, t, env);
      return { state: s, bankroll: env.bankroll };
    }
    if (s.phase !== 'play' || s.step !== 'claim' || !s.pending) return { state: s, bankroll: env.bankroll };
    if (optionId === 'kong' && yourClaims(s).some((o) => o.id === 'kong')) return claimKong(s, 0, env);
    if (optionId === 'pass') return resolveDiscard(s, env, true);
    if (optionId === 'giveup') return giveUp(s, env);
    if (optionId === 'win') {
      const win = winWith(s, 0, s.pending.tile, false);
      if (!win) return { state: s, bankroll: env.bankroll };
      const seats = s.seats.map((x, k) => (k === 0 ? { ...x, hand: sorted([...x.hand, s.pending!.tile]) } : x));
      return settle({ ...s, seats }, 0, 'discard', s.pending.by, win, env);
    }
    if (optionId === 'pung' && yourClaims(s).some((o) => o.id === 'pung')) return { state: claim(s, 0, 'pung', s.pending.tile), bankroll: env.bankroll };
    if (optionId.startsWith('chow:') && yourClaims(s).some((o) => o.id === optionId)) {
      return { state: claim(s, 0, 'chow', Number(optionId.slice(5))), bankroll: env.bankroll };
    }
    return { state: s, bankroll: env.bankroll };
  },
  tick(s, env) {
    if (s.phase !== 'play' || s.step === 'claim' || s.step === 'kong' || env.now < s.nextAt) return null;
    if (s.turn === 0) return s.step === 'draw' ? drawTile(s, env) : null;
    if (s.step === 'draw') return drawTile(s, env);
    // An AI that just claimed a tile discards.
    return discard(s, s.turn, chooseDiscard(s, s.turn), env);
  },
  gesture(s, gesture, env) {
    if (s.phase !== 'play' || s.step === 'claim' || s.step === 'kong') return null;
    // The discard piles: scroll pages through the players (then the give-up
    // page), a tap goes back — or, on the give-up page, gives up.
    if (s.pile != null) {
      if (gesture === 'up' || gesture === 'down') {
        const pile = (s.pile + (gesture === 'up' ? -1 : 1) + PAGES) % PAGES;
        return { state: { ...s, pile }, bankroll: env.bankroll };
      }
      if (gesture === 'tap' && s.pile === GIVE_UP_PAGE) return giveUp(s, env);
      return { state: { ...s, pile: null }, bankroll: env.bankroll };
    }
    // Double-tap declares a self-drawn win if there is one, else opens the piles.
    if (gesture === 'double' && !selfDrawWin(s)) return { state: { ...s, pile: 0, confirm: false }, bankroll: env.bankroll };
    if (s.turn !== 0 || s.step !== 'discard') return null;
    const hand = s.seats[0]!.hand;
    if (gesture === 'up' || gesture === 'down') {
      const cursor = (s.cursor + (gesture === 'up' ? -1 : 1) + hand.length) % hand.length;
      return { state: { ...s, cursor, confirm: false }, bankroll: env.bankroll };
    }
    if (gesture === 'tap') {
      if (!s.confirm) return { state: { ...s, confirm: true }, bankroll: env.bankroll };
      return discard(s, 0, hand[s.cursor]!, env);
    }
    // Only a tile you drew can be a self-drawn win (not a claimed one).
    const win = selfDrawWin(s);
    return win ? settle(s, 0, 'selfDraw', undefined, win, env) : { state: { ...s, confirm: false }, bankroll: env.bankroll };
  },
  leftPane(s) {
    if (s.phase !== 'play' || s.step === 'claim' || s.step === 'kong') return null;
    const rows = handRows(s, s.turn === 0 && s.step === 'discard');
    if (s.drawn !== null && s.turn === 0) rows.push(`Drew: ${tileLong(s.drawn)}${s.luckyDraw ? ' ★' : ''}`);
    return rows.join('\n');
  },
};
